import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { InvoicesBookService, InvoiceBookItem, CreateManualInvoiceRequest, InvoiceDetails, NextInvoiceNumber } from '../../../services/apps/invoices/invoices-book.service';
import { InvoicesService } from '../../../services/apps/invoices/invoices.service';
import { TaxesService, Tax } from '../../../services/apps/taxes/taxes.service';
import {
  computeInvoiceLine,
  filterTaxesForInvoice,
  normalizeTenantTaxMode
} from '../../../utils/invoice-tax-computation';
import { InvoiceItemDialogComponent } from './invoice-item-dialog.component';
import { CancelInvoiceDialogComponent } from './cancel-invoice-dialog.component';
import { FiscalDocumentLinesComponent, FiscalDocumentLineItem } from './fiscal-document-lines.component';
import { FiscalDocumentTotalsComponent } from './fiscal-document-totals.component';
import { CustomersService } from '../../../services/apps/customers/customers.service';
import { FiscalService } from '../../../services/apps/fiscal/fiscal.service';
import { getApiErrorMessage } from '../../../utils/api-error';
import { CashBoxService } from '../../../services/cash-box.service';
import { PaymentMethodsService, PaymentMethod } from '../../../services/payment-methods.service';
import { ToastrService } from 'ngx-toastr';
import { Observable, debounceTime, distinctUntilChanged, switchMap, of, map } from 'rxjs';
import { finalize } from 'rxjs/operators';
import * as QRCode from 'qrcode';

export interface ManualInvoiceDialogData {
  mode: 'create' | 'view';
  invoice?: InvoiceBookItem;
  showCancelButton?: boolean;
  reverseMode?: boolean; // Nuevo: indica que estamos en modo reversión de factura
  customerId?: string; // Para pre-seleccionar cliente
  initialItems?: {
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
  }[]; // Para pre-cargar líneas de factura
  abonoId?: string; // Para facturación de resto del mes
}

@Component({
  selector: 'app-manual-invoice-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRadioModule,
    MatDividerModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatCardModule,
    MatExpansionModule,
    TablerIconsModule,
    NgxMatSelectSearchModule,
    FiscalDocumentLinesComponent,
    FiscalDocumentTotalsComponent
  ],
  templateUrl: './manual-invoice-dialog.component.html',
  styleUrls: ['./manual-invoice-dialog.component.scss']
})
export class ManualInvoiceDialogComponent implements OnInit {
  invoiceForm!: FormGroup;
  loading = false;
  isViewMode = false;
  showCancelButton = false;
  reverseMode = false; // Nuevo: modo reversión
  customerReadOnly = false; // Nuevo: controlar si el cliente es de solo lectura

  customers$!: Observable<any[]>;
  selectedCustomer: any = null;
  filteredCustomers: any[] = [];
  allCustomers: any[] = [];
  customerSearchCtrl = new FormControl();
  
  nextInvoiceNumber: any = null;
  loadingNumber = false;
  
  // Estado de carga para datos de la factura
  loadingInvoiceData = false;
  loadingCustomers = false;

  /** Detalle fiscal completo (modo vista) para AlicIvas / Tributos. */
  viewInvoiceFiscal: InvoiceDetails | null = null;
  authorizationQrDataUrl: string | null = null;

  /** Vencimientos de la factura (modo vista). */
  invoiceDueDates: {
    invoiceTotal: number;
    isOverdue: boolean;
    appliedSurchargeLevel: number;
    dueDates: {
      dueDateNumber: number;
      dueDate: string;
      baseAmount: number;
      surchargePercent: number;
      surchargeFixed: number;
      totalAmount: number;
      triggersSuspension: boolean;
      status: string;
    }[];
  } | null = null;
  loadingDueDates = false;

  /** Modo fiscal del tenant (None|Ticket|Electronic) en alta; en vista se toma de la factura. */
  tenantTaxMode: string | null = null;
  allTenantTaxes: Tax[] = [];
  applicableTaxesForInvoice: Tax[] = [];
  consumerIdentificationThreshold = 0;
  receiverDocNumberControl = new FormControl('');
  
  // Array para mostrar en la tabla
  itemsDataSource: any[] = [];
  
  // Caja abierta activa
  activeCashBox: any = null;
  loadingCashBox = false;
  noCashBoxError = false;

  documentTypes = [
    { value: 'FA', label: 'Factura A' },
    { value: 'FB', label: 'Factura B' },
    { value: 'FC', label: 'Factura C' }
  ];

  paymentMethods: { value: string, label: string }[] = [];
  loadingPaymentMethods = false;


  constructor(
    public dialogRef: MatDialogRef<ManualInvoiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManualInvoiceDialogData,
    private fb: FormBuilder,
    private invoicesBookService: InvoicesBookService,
    private invoicesService: InvoicesService,
    private customersService: CustomersService,
    private cashBoxService: CashBoxService,
    private paymentMethodsService: PaymentMethodsService,
    private taxesService: TaxesService,
    private fiscalService: FiscalService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {
    this.isViewMode = data.mode === 'view';
    this.showCancelButton = data.showCancelButton || false;
    this.reverseMode = data.reverseMode || false;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadPaymentMethods();
    
    if (this.isViewMode && this.data.invoice) {
      this.loadInvoiceData();
    } else {
      this.loadTenantTaxesCatalog();
      // Si se proporciona customerId, marcar como readonly
      if (this.data.customerId) {
        this.customerReadOnly = true;
      }
      
      this.setupCustomerSearch();
      this.loadActiveCashBox();
      this.fiscalService.getConsumerIdentificationThreshold().subscribe({
        next: t => {
          this.consumerIdentificationThreshold = t.threshold;
          this.updateReceiverDocValidators();
        },
        error: () => this.consumerIdentificationThreshold = 0
      });
      
      // Los items iniciales se cargan después de pre-seleccionar el cliente
    }
  }

  // La caja abierta se maneja automáticamente en el backend

  initForm(): void {
    this.invoiceForm = this.fb.group({
      selectedCustomer: ['', Validators.required],
      customerId: ['', Validators.required],
      invoiceType: ['CASH', Validators.required],
      documentTypeCode: ['FB', Validators.required],
      issueDate: [{ value: new Date(), disabled: true }, Validators.required],
      items: this.fb.array([]),
      
      // Payment info (solo para CASH)
      paymentMethod: ['0'], // 0 = Efectivo por defecto
      
      // Readonly fields
      posCode: [{ value: '', disabled: true }],
      invoiceNumber: [{ value: '', disabled: true }],
      subtotal: [{ value: 0, disabled: true }],
      taxTotal: [{ value: 0, disabled: true }],
      total: [{ value: 0, disabled: true }]
    });

    // Initialize items data source
    this.updateItemsDataSource();

    // Subscribe to invoice type changes
    this.invoiceForm.get('invoiceType')?.valueChanges.subscribe(type => {
      this.updatePaymentValidators(type);
    });

    // Subscribe to items changes to recalculate totals
    this.items.valueChanges.subscribe(() => {
      this.calculateTotals();
    });
  }

  get items(): FormArray {
    return this.invoiceForm.get('items') as FormArray;
  }

  /** Modo fiscal efectivo (vista: factura cargada; alta: respuesta next-number / null). */
  effectiveTaxMode(): string | null {
    if (this.isViewMode && this.viewInvoiceFiscal?.tenantTaxMode) {
      return this.viewInvoiceFiscal.tenantTaxMode;
    }
    return this.tenantTaxMode;
  }

  /** None: ocultar bloque fiscal de impuestos (AC-2). */
  isTaxModeNone(): boolean {
    return normalizeTenantTaxMode(this.effectiveTaxMode()) === 'None';
  }

  loadTenantTaxesCatalog(): void {
    this.taxesService.getTaxes(true, { silent: true }).subscribe({
      next: (taxes) => {
        this.allTenantTaxes = taxes ?? [];
        this.refreshApplicableTaxes();
      },
      error: () => {
        this.allTenantTaxes = [];
        this.refreshApplicableTaxes();
      }
    });
  }

  refreshApplicableTaxes(): void {
    this.applicableTaxesForInvoice = filterTaxesForInvoice(this.tenantTaxMode, this.allTenantTaxes);
    this.items.controls.forEach(c => {
      const desc = (c.get('description')?.value ?? '') as string;
      if (desc.trim()) {
        this.calculateItemTotal(c as FormGroup);
      }
    });
    this.calculateTotals();
    this.updateItemsDataSource();
  }

  createItem(): FormGroup {
    return this.fb.group({
      description: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      taxRate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      subtotal: [{ value: 0, disabled: true }],
      taxAmount: [{ value: 0, disabled: true }],
      total: [{ value: 0, disabled: true }],
      lineTaxes: [[] as unknown[]]
    });
  }

  addItem(): void {
    const item = this.createItem();
    
    // Subscribe to quantity and unitPrice changes
    item.get('quantity')?.valueChanges.subscribe(() => this.calculateItemTotal(item));
    item.get('unitPrice')?.valueChanges.subscribe(() => this.calculateItemTotal(item));
    item.get('taxRate')?.valueChanges.subscribe(() => this.calculateItemTotal(item));
    
    this.items.push(item);
  }

  openAddItemDialog(): void {
    const dialogRef = this.dialog.open(InvoiceItemDialogComponent, {
      width: '600px',
      data: {
        mode: 'create',
        invoiceTaxContext: {
          tenantTaxMode: this.tenantTaxMode,
          applicableTaxes: this.applicableTaxesForInvoice,
          allTenantTaxes: this.allTenantTaxes
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        console.log('Dialog result:', result); // Debug log
        
        const item = this.createItem();
        
        // Subscribe to changes for calculations BEFORE patching values
        item.get('quantity')?.valueChanges.subscribe(() => this.calculateItemTotal(item as FormGroup));
        item.get('unitPrice')?.valueChanges.subscribe(() => this.calculateItemTotal(item as FormGroup));
        item.get('taxRate')?.valueChanges.subscribe(() => this.calculateItemTotal(item as FormGroup));
        
        // Patch values AFTER setting up subscriptions
        item.patchValue(result.item);
        
        console.log('Item after patchValue:', item.value); // Debug log
        
        this.items.push(item);
        
        // Calculate totals for the new item
        this.calculateItemTotal(item as FormGroup);
        this.calculateTotals();
        
        // Update the data source for the table
        this.updateItemsDataSource();
        
        console.log('Item after calculation:', item.value); // Debug log
      }
    });
  }

  openEditItemDialog(index: number): void {
    const item = this.items.at(index);
    const dialogRef = this.dialog.open(InvoiceItemDialogComponent, {
      width: '600px',
      data: { 
        mode: 'edit', 
        item: item.value,
        index: index,
        invoiceTaxContext: {
          tenantTaxMode: this.tenantTaxMode,
          applicableTaxes: this.applicableTaxesForInvoice,
          allTenantTaxes: this.allTenantTaxes
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        // Patch values first
        item.patchValue(result.item);
        
        // Then calculate totals
        this.calculateItemTotal(item as FormGroup);
        this.calculateTotals();
        
        // Update the data source for the table
        this.updateItemsDataSource();
      }
    });
  }

  removeItem(index: number): void {
    if (this.items.length > 1) {
      this.items.removeAt(index);
      this.calculateTotals();
      this.updateItemsDataSource();
    }
  }

  updateItemsDataSource(): void {
    this.itemsDataSource = this.items.controls
      .map(control => {
        const itemValue = control.getRawValue();
        if (!itemValue.description || String(itemValue.description).trim() === '') {
          return null;
        }
        return {
          ...itemValue,
          subtotal: control.get('subtotal')?.value ?? itemValue.subtotal,
          taxAmount: control.get('taxAmount')?.value ?? itemValue.taxAmount,
          total: control.get('total')?.value ?? itemValue.total,
          lineTaxes: (control.get('lineTaxes')?.value ?? itemValue.lineTaxes ?? []) as unknown[]
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  calculateItemTotal(item: FormGroup): void {
    const quantity = Number(item.get('quantity')?.value) || 0;
    const unitPriceFinal = Number(item.get('unitPrice')?.value) || 0;
    const gross = quantity * unitPriceFinal;

    const taxesToApply = this.applicableTaxesForInvoice.length > 0
      ? this.applicableTaxesForInvoice
      : this.allTenantTaxes;

    if (taxesToApply.length > 0) {
      const computed = computeInvoiceLine(quantity, gross, taxesToApply);
      const lineTotal = Math.round((computed.lineNetTotal + computed.lineTaxTotal) * 100) / 100;
      item.get('subtotal')?.setValue(computed.lineNetTotal, { emitEvent: false });
      item.get('taxAmount')?.setValue(computed.lineTaxTotal, { emitEvent: false });
      item.get('total')?.setValue(lineTotal, { emitEvent: false });
      item.get('lineTaxes')?.setValue(computed.lineTaxes, { emitEvent: false });
    } else {
      const taxRate = Number(item.get('taxRate')?.value) || 0;
      const unitPriceNet = unitPriceFinal / (1 + (taxRate / 100));
      const subtotal = quantity * unitPriceNet;
      const taxAmount = subtotal * (taxRate / 100);
      const total = subtotal + taxAmount;
      item.get('subtotal')?.setValue(subtotal, { emitEvent: false });
      item.get('taxAmount')?.setValue(taxAmount, { emitEvent: false });
      item.get('total')?.setValue(total, { emitEvent: false });
      item.get('lineTaxes')?.setValue([], { emitEvent: false });
    }

    this.calculateTotals();
  }

  get fiscalLines(): FiscalDocumentLineItem[] {
    return this.itemsDataSource as FiscalDocumentLineItem[];
  }

  get invoiceTaxBreakdown(): { taxName: string; taxAmount: number }[] {
    const map = new Map<string, number>();
    this.items.controls.forEach(item => {
      const desc = (item.get('description')?.value ?? '') as string;
      if (!desc.trim()) return;
      const lineTaxes: { taxName: string; taxAmount: number }[] = item.get('lineTaxes')?.value ?? [];
      lineTaxes.forEach(t => {
        map.set(t.taxName, (map.get(t.taxName) ?? 0) + t.taxAmount);
      });
    });
    return Array.from(map.entries()).map(([taxName, taxAmount]) => ({ taxName, taxAmount }));
  }

  calculateTotals(): void {
    let subtotal = 0;
    let taxTotal = 0;
    let total = 0;

    // Only calculate totals for items that have a description
    this.items.controls.forEach(item => {
      const itemValue = item.value;
      if (itemValue.description && itemValue.description.trim() !== '') {
        subtotal += item.get('subtotal')?.value || 0;
        taxTotal += item.get('taxAmount')?.value || 0;
        total += item.get('total')?.value || 0;
      }
    });

    this.invoiceForm.patchValue({
      subtotal: subtotal,
      taxTotal: taxTotal,
      total: total
    }, { emitEvent: false });
    this.updateReceiverDocValidators();
  }

  get requiresConsumerIdentification(): boolean {
    const total = Number(this.invoiceForm.get('total')?.value ?? 0);
    return this.needsConsumerIdentification(total) && this.isCfCustomerWithoutDoc(this.selectedCustomer);
  }

  private updateReceiverDocValidators(): void {
    if (this.requiresConsumerIdentification) {
      this.receiverDocNumberControl.setValidators([Validators.required, Validators.minLength(7)]);
    } else {
      this.receiverDocNumberControl.clearValidators();
      this.receiverDocNumberControl.setValue('', { emitEvent: false });
    }
    this.receiverDocNumberControl.updateValueAndValidity({ emitEvent: false });
  }

  setupCustomerSearch(): void {
    this.loadingCustomers = true;

    // Load all customers initially
    this.customersService.getCustomersPaged(1, 1000, '').pipe(
      finalize(() => {
        this.loadingCustomers = false;
      })
    ).subscribe({
      next: (response) => {
        if (response.success && response.requestResult) {
          this.allCustomers = response.requestResult.items || [];
          this.filteredCustomers = this.allCustomers;
          
          // Después de cargar los clientes, pre-seleccionar si hay customerId
          if (this.data.customerId) {
            this.preselectCustomer(this.data.customerId);
          }
          
          // Después de pre-seleccionar el cliente, cargar items iniciales
          if (this.data.initialItems && this.data.initialItems.length > 0) {
            this.loadInitialItems();
          }
        }
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        this.toastr.error('Error al cargar clientes', 'Error');
      }
    });

    // Setup real-time search filter
    this.customerSearchCtrl.valueChanges.pipe(
      debounceTime(100),
      distinctUntilChanged()
    ).subscribe((searchTerm: string) => {
      this.filterCustomers(searchTerm);
    });
  }

  filterCustomers(searchTerm: string): void {
    console.log('Filtering with term:', searchTerm);
    
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredCustomers = [...this.allCustomers];
      console.log('No search term, showing all customers:', this.filteredCustomers.length);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    this.filteredCustomers = this.allCustomers.filter(customer => {
      const fullName = `${customer.firstName} ${customer.surname}`.toLowerCase();
      const docNumber = customer.docNumber ? customer.docNumber.toLowerCase() : '';
      
      const matches = customer.firstName.toLowerCase().includes(term) ||
             customer.surname.toLowerCase().includes(term) ||
             docNumber.includes(term) ||
             fullName.includes(term);
      
      if (matches) {
        console.log('Customer matches:', customer.firstName, customer.surname);
      }
      
      return matches;
    });
    
    console.log('Filtered customers count:', this.filteredCustomers.length);
  }

  onSelectOpened(opened: boolean): void {
    if (opened) {
      // Reset search when opening
      this.customerSearchCtrl.setValue('');
      this.filteredCustomers = this.allCustomers;
    }
  }

  displayCustomer(customer: any): string {
    return customer ? `${customer.firstName} ${customer.surname} - ${customer.docNumber}` : '';
  }

  getDocumentTypeLabel(documentTypeCode: string): string {
    const documentType = this.documentTypes.find(type => type.value === documentTypeCode);
    return documentType ? documentType.label : documentTypeCode;
  }

  /** Texto accesible del título del diálogo (sin mensajes tipo «Seleccione un cliente»). */
  getInvoiceTitleAriaLabel(): string {
    const parts: string[] = [];
    const typeLabel = this.getDocumentTypeLabel(this.invoiceForm.get('documentTypeCode')?.value ?? '');
    if (typeLabel) {
      parts.push(`Comprobante ${typeLabel}`);
    }
    const num = this.getInvoiceNumberDisplay();
    if (num) {
      parts.push(`número ${num}`);
    }
    const dateStr = this.formatIssueDateTitle();
    if (dateStr) {
      parts.push(`emisión ${dateStr}`);
    }
    return parts.join(', ') || 'Factura manual';
  }

  private formatIssueDateTitle(): string {
    const raw = this.invoiceForm.get('issueDate')?.value as Date | string | null | undefined;
    if (raw === null || raw === undefined) {
      return '';
    }
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  onCustomerSelected(event: any): void {
    const customer = event.value;
    this.selectedCustomer = customer;
    this.updateReceiverDocValidators();
    this.invoiceForm.patchValue({
      customerId: customer.id
    });
    
    // Get next invoice number for this customer
    this.getNextInvoiceNumber(customer.id);
  }

  getNextInvoiceNumber(customerId: string): void {
    const openingId = this.activeCashBox?.id;
    if (!openingId) {
      this.toastr.warning('Abrí una caja para obtener el número de comprobante.', 'Caja');
      this.loadingNumber = false;
      return;
    }

    this.loadingNumber = true;

    this.invoicesBookService.getNextInvoiceNumber(customerId, openingId, false).subscribe({
      next: (response) => {
        if (response.success && response.body) {
          this.nextInvoiceNumber = response.body;
          const body = response.body;
          this.tenantTaxMode = body.tenantTaxMode ?? null;
          this.refreshApplicableTaxes();
          this.invoiceForm.patchValue({
            posCode: body.posCode,
            invoiceNumber: body.number,
            documentTypeCode: body.documentTypeCode
          });
        }
        this.loadingNumber = false;
      },
      error: (error) => {
        console.error('Error getting next invoice number:', error);
        this.toastr.error('Error al obtener el próximo número de factura', 'Error');
        this.loadingNumber = false;
      }
    });
  }

  preselectCustomer(customerId: string): void {
    // Buscar el cliente en la lista de todos los clientes
    const customer = this.allCustomers.find(c => c.id === customerId);
    if (customer) {
      this.selectedCustomer = customer;
      this.updateReceiverDocValidators();
      this.invoiceForm.patchValue({
        selectedCustomer: customer,
        customerId: customer.id
      });
      this.getNextInvoiceNumber(customer.id);
    }
  }

  loadInitialItems(): void {
    if (!this.data.initialItems) return;

    this.data.initialItems.forEach(itemData => {
      const item = this.createItem();
      
      // Subscribe to changes for calculations BEFORE patching values
      item.get('quantity')?.valueChanges.subscribe(() => this.calculateItemTotal(item));
      item.get('unitPrice')?.valueChanges.subscribe(() => this.calculateItemTotal(item));
      item.get('taxRate')?.valueChanges.subscribe(() => this.calculateItemTotal(item));
      
      // Patch values AFTER setting up subscriptions
      item.patchValue({
        description: itemData.description,
        quantity: itemData.quantity,
        unitPrice: itemData.unitPrice,
        taxRate: itemData.taxRate
      });
      
      this.items.push(item);
      
      // Calculate totals for the new item
      this.calculateItemTotal(item);
    });
    
    // Update the data source for the table and calculate overall totals
    this.calculateTotals();
    this.updateItemsDataSource();
  }

  updatePaymentValidators(invoiceType: string): void {
    const paymentMethod = this.invoiceForm.get('paymentMethod');

    if (invoiceType === 'CASH') {
      paymentMethod?.setValidators([Validators.required]);
    } else {
      paymentMethod?.clearValidators();
    }

    paymentMethod?.updateValueAndValidity();
  }

  loadActiveCashBox(): void {
    this.loadingCashBox = true;
    this.noCashBoxError = false;
    
    this.cashBoxService.getOpenCashBoxes().subscribe({
      next: (response: any) => {
        if (response.success && response.body && response.body.length > 0) {
          this.activeCashBox = {
            ...response.body[0],
            cashBoxName: response.body[0].cashBoxDescription // Mapear cashBoxDescription a cashBoxName
          };
          console.log('Active cash box loaded:', this.activeCashBox);
          // Si el cliente ya estaba elegido antes de que llegue la caja, repetir next-number con openingId.
          const cid = this.selectedCustomer?.id ?? this.invoiceForm?.get('customerId')?.value;
          if (cid) {
            this.getNextInvoiceNumber(cid);
          }
        } else {
          this.noCashBoxError = true;
          this.toastr.warning('No hay cajas abiertas. Abre una caja para poder facturar.', 'Caja Cerrada');
        }
        this.loadingCashBox = false;
      },
      error: (error: any) => {
        console.error('Error loading cash box:', error);
        this.noCashBoxError = true;
        this.toastr.error('Error al cargar la caja abierta', 'Error');
        this.loadingCashBox = false;
      }
    });
  }

  loadPaymentMethods(): void {
    // Evitar cargar múltiples veces si ya está cargando
    if (this.loadingPaymentMethods) {
      return;
    }

    this.loadingPaymentMethods = true;
    this.paymentMethodsService.getActivePaymentMethods().subscribe({
      next: (response) => {
        if (response.success && response.requestResult) {
          // Mapear los métodos de pago al formato esperado por el componente
          // Eliminar duplicados basándose en el ID
          const uniqueMethods = new Map<string, { value: string, label: string, displayOrder: number }>();

          response.requestResult.forEach(pm => {
            if (!uniqueMethods.has(pm.id)) {
              uniqueMethods.set(pm.id, {
                value: pm.id, // Usar el ID del método de pago
                label: pm.name,
                displayOrder: pm.displayOrder || 0
              });
            }
          });
          
          // Convertir el Map a Array y ordenar por displayOrder
          this.paymentMethods = Array.from(uniqueMethods.values())
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(m => ({ value: m.value, label: m.label }));
          
          // Si hay métodos disponibles y no hay uno seleccionado, seleccionar el primero
          if (this.paymentMethods.length > 0 && !this.invoiceForm.get('paymentMethod')?.value) {
            this.invoiceForm.patchValue({
              paymentMethod: this.paymentMethods[0].value
            });
          }
        }
        this.loadingPaymentMethods = false;
      },
      error: (error) => {
        console.error('Error loading payment methods:', error);
        this.toastr.error('Error al cargar los métodos de pago');
        this.loadingPaymentMethods = false;
        // Fallback a métodos por defecto
        this.paymentMethods = [
          { value: '0', label: 'Efectivo' }
        ];
      }
    });
  }

  loadInvoiceData(): void {
    if (!this.data.invoice) return;

    this.loadingInvoiceData = true;
    console.log('Loading invoice data for ID:', this.data.invoice.id);

    // Cargar detalles completos de la factura desde la API
    this.invoicesBookService.getInvoiceDetails(this.data.invoice.id).subscribe({
      next: (response) => {
        console.log('Invoice details response:', response);
        if (response.success && response.body) {
          const invoice = response.body;
          this.viewInvoiceFiscal = invoice;
          void this.renderAuthorizationQr(invoice);
          console.log('Invoice data:', invoice);
          console.log('Invoice items:', invoice.items);
          
          // Usar datos del cliente que ya vienen en la factura
          console.log('Invoice customerId:', invoice.customerId);
          console.log('Invoice customerName:', invoice.customerName);
          
          // Crear objeto cliente con los datos de la factura
          const customerData = {
            id: invoice.customerId,
            companyName: invoice.customerName,
            companyAddress: '',
            companyPhone: '',
            contactName: '',
            contactSurname: '',
            contactEmail: '',
            contactPhone: '',
            documentNumber: invoice.customerDocNumber,
            documentType: 'DNI' // Asumir DNI por defecto
          };
          
          this.selectedCustomer = customerData;
          this.invoiceForm.patchValue({
            selectedCustomer: customerData,
            customerId: invoice.customerId,
            invoiceType: invoice.invoiceType,
            documentTypeCode: invoice.documentTypeCode,
            issueDate: new Date(invoice.issueDate),
            posCode: invoice.posCode ?? '',
            invoiceNumber: invoice.number,
            subtotal: invoice.subtotal,
            taxTotal: invoice.taxTotal,
            total: invoice.total,
            paymentMethod: invoice.paymentMethodId || this.paymentMethods[0]?.value || ''
          });

          // Cargar items de la factura
          console.log('Loading invoice items:', invoice.items);
          this.loadInvoiceItems(invoice.items);

          // Cargar vencimientos solo para cuenta corriente
          if (this.isCurrentAccountInvoice(invoice.invoiceType)) {
            this.loadDueDates(this.data.invoice!.id);
          }

          // Marcar como cargado
          this.loadingInvoiceData = false;
        } else {
          console.error('No invoice data received:', response);
          this.toastr.error('No se pudieron cargar los detalles de la factura', 'Error');
          this.loadingInvoiceData = false;
        }
      },
      error: (error) => {
        console.error('Error loading invoice details:', error);
        this.toastr.error('Error al cargar los detalles de la factura', 'Error');
        this.loadingInvoiceData = false;
      }
    });

    // Deshabilitar el formulario en modo view
    this.invoiceForm.disable();
  }

  loadDueDates(invoiceId: string): void {
    this.loadingDueDates = true;
    this.invoicesBookService.getInvoiceDueDates(invoiceId).subscribe({
      next: (res: any) => {
        const data = res?.body ?? res;
        this.invoiceDueDates = data ?? null;
        this.loadingDueDates = false;
      },
      error: () => { this.loadingDueDates = false; }
    });
  }

  loadInvoiceItems(items: any[]): void {
    console.log('loadInvoiceItems called with:', items);
    console.log('Items length:', items?.length);
    
    // Limpiar items existentes
    while (this.items.length !== 0) {
      this.items.removeAt(0);
    }

    if (!items || items.length === 0) {
      console.log('No items to load');
      this.updateItemsDataSource();
      return;
    }

    // Agregar items de la factura
    items.forEach((item, index) => {
      console.log(`Loading item ${index}:`, item);
      const itemForm = this.fb.group({
        description: [item.description, Validators.required],
        quantity: [item.quantity, [Validators.required, Validators.min(0.01)]],
        unitPrice: [item.unitPrice, [Validators.required, Validators.min(0)]],
        taxRate: [item.taxRate ?? 0, [Validators.required, Validators.min(0), Validators.max(100)]],
        subtotal: [{ value: item.subtotal, disabled: true }],
        taxAmount: [{ value: item.taxAmount, disabled: true }],
        total: [{ value: item.total, disabled: true }],
        lineTaxes: [item.lineTaxes ?? []]
      });
      
      this.items.push(itemForm);
    });

    console.log('Items after loading:', this.items.length);
    // Actualizar la fuente de datos de la tabla
    this.updateItemsDataSource();
  }

  onSubmit(): void {
    if (this.invoiceForm.invalid) {
      this.invoiceForm.markAllAsTouched();
      this.toastr.error('Por favor complete todos los campos requeridos', 'Error');
      return;
    }

    const formValue = this.invoiceForm.getRawValue();

    if (this.requiresConsumerIdentification && this.receiverDocNumberControl.invalid) {
      this.receiverDocNumberControl.markAsTouched();
      this.toastr.error('Ingresá el DNI del consumidor final para este monto.', 'Identificación requerida');
      return;
    }

    if (!this.activeCashBox?.id) {
      this.toastr.error('Abrí una caja antes de emitir una factura manual.', 'Caja');
      return;
    }

    this.loading = true;

    const openingId = this.activeCashBox.id;
    this.invoicesBookService.getNextInvoiceNumber(formValue.customerId, openingId, true).subscribe({
      next: (numberResponse) => {
        if (numberResponse.success && numberResponse.body) {
          const nb = numberResponse.body;
          if (nb.tenantTaxMode) {
            this.tenantTaxMode = nb.tenantTaxMode;
            this.refreshApplicableTaxes();
          }
          // Usar el número reservado
          const reservedNumber = nb.number;
          
          const request: CreateManualInvoiceRequest = {
            customerId: formValue.customerId,
            invoiceType: formValue.invoiceType,
            documentTypeCode: formValue.documentTypeCode,
            posCode: formValue.posCode,
            invoiceNumber: reservedNumber, // Usar el número reservado
            issueDate: this.formatDateForAPI(formValue.issueDate),
            currency: 'ARS',
            subtotal: formValue.subtotal,
            taxTotal: formValue.taxTotal,
            total: formValue.total,
            items: formValue.items.map((item: any) => ({
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              subtotal: item.subtotal,
              taxRate: item.taxRate,
              taxAmount: item.taxAmount,
              total: item.total
            })),
            payment: formValue.invoiceType === 'CASH' ? {
              paymentMethod: this.paymentMethods[0]?.value ?? formValue.paymentMethod,
              amount: formValue.total,
              paymentDate: this.formatDateForAPI(new Date())
            } : undefined,
            cashBoxOpeningId: this.activeCashBox.id,
            receiverDocType: this.requiresConsumerIdentification ? 'DNI' : undefined,
            receiverDocNumber: this.requiresConsumerIdentification
              ? String(this.receiverDocNumberControl.value ?? '').trim()
              : undefined
          };

          this.invoicesBookService.createManualInvoice(request).subscribe({
            next: (response) => {
              if (response.success) {
                this.toastr.success('Factura creada exitosamente', 'Éxito');
                this.dialogRef.close(true);
              } else {
                this.toastr.error(response.reason || 'Error al crear factura', 'Error');
              }
              this.loading = false;
            },
            error: (error) => {
              console.error('Error creating invoice:', error);
              const err = error?.error;
              const errorMsg =
                err?.message ||
                err?.reason ||
                error.message ||
                'Error al crear factura';
              this.toastr.error(errorMsg, 'Error');
              this.loading = false;
            }
          });
        } else {
          this.toastr.error('Error al reservar número de factura', 'Error');
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Error reserving invoice number:', error);
        this.toastr.error('Error al reservar número de factura', 'Error');
        this.loading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getCustomerDisplayName(): string {
    const raw =
      this.data.invoice?.customerName ??
      this.selectedCustomer?.companyName ??
      (this.selectedCustomer
        ? `${this.selectedCustomer.firstName ?? ''} ${this.selectedCustomer.surname ?? ''}`.trim()
        : '');
    if (!raw) {
      return 'Cliente no disponible';
    }
    return this.toTitleCase(raw);
  }

  private toTitleCase(value: string): string {
    return value
      .split(/\s+/)
      .map(word =>
        word
          .split(/(-|')/)
          .map(part => (part === '-' || part === "'" ? part : this.capitalizeWord(part)))
          .join('')
      )
      .join(' ');
  }

  private capitalizeWord(word: string): string {
    if (!word) {
      return word;
    }
    return word.charAt(0).toLocaleUpperCase('es') + word.slice(1).toLocaleLowerCase('es');
  }

  shouldShowInvoiceTypeChip(): boolean {
    if (!this.data.invoice) {
      return false;
    }
    const label = this.getInvoiceTypeLabel(this.data.invoice.type || this.data.invoice.invoiceType);
    const normalized = label.trim().toUpperCase();
    return normalized !== '' && normalized !== 'N/A';
  }

  getCustomerDocument(): string {
    if (this.data.invoice?.customerDocNumber) {
      return this.data.invoice.customerDocNumber;
    }
    return 'Documento no disponible';
  }

  getInvoiceNumberDisplay(): string {
    // En modo visualización, formatear con posCode + number
    if (this.isViewMode && this.data.invoice) {
      const inv = this.data.invoice;
      const posCode = inv.posCode ?? this.invoiceForm.get('posCode')?.value;
      const number = inv.number ?? this.invoiceForm.get('invoiceNumber')?.value;
      if (posCode && number) {
        const pc = String(posCode).padStart(4, '0');
        const nm = String(Math.trunc(Number(number))).padStart(8, '0');
        return `${pc}-${nm}`;
      }
      if (number) return String(number);
    }

    const nb = this.nextInvoiceNumber as NextInvoiceNumber | null | undefined;
    const raw = nb?.formattedFiscalNumber?.trim();
    // Ignorar placeholder "0000-00000000" (PV/correlativo 0 en servidor); usar partes si vienen bien.
    const formatted =
      raw && !this.isAllZeroFiscalDisplay(raw) ? raw : undefined;
    const fromParts = this.formatFiscalFromParts(nb);
    if (formatted) {
      return formatted;
    }
    if (fromParts) {
      return fromParts;
    }

    const formValue = this.invoiceForm.get('invoiceNumber')?.value;
    if (formValue !== '' && formValue != null) {
      return String(formValue);
    }

    return this.isViewMode ? 'Número no disponible' : '';
  }

  private isAllZeroFiscalDisplay(s: string): boolean {
    const m = /^(\d+)\s*-\s*(\d+)$/.exec(s.trim());
    if (!m) {
      return false;
    }
    return parseInt(m[1], 10) === 0 && parseInt(m[2], 10) === 0;
  }

  private formatFiscalFromParts(nb: NextInvoiceNumber | null | undefined): string | null {
    if (!nb) {
      return null;
    }
    const pos = parseInt(String(nb.posCode ?? ''), 10);
    const num = Number(nb.number);
    if (!Number.isFinite(pos) || pos <= 0 || !Number.isFinite(num) || num <= 0) {
      return null;
    }
    const pc = String(pos).padStart(4, '0');
    const nm = String(Math.trunc(num)).padStart(8, '0');
    return `${pc}-${nm}`;
  }

  cancelInvoice(): void {
    if (!this.data.invoice) return;

    // Verificar si la factura ya está cancelada
    if (this.data.invoice.status === 'Canceled') {
      this.toastr.warning('Esta factura ya está cancelada', 'Advertencia');
      return;
    }

    // Si estamos en modo reversión, llamar al método de reversión
    if (this.reverseMode) {
      this.performReverseInvoice();
      return;
    }

    // Mostrar diálogo de confirmación para cancelación normal
    const dialogRef = this.dialog.open(CancelInvoiceDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        invoice: this.data.invoice,
        reversesPlanCoverage: this.viewInvoiceFiscal?.reversesPlanCoverage
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed) {
        this.performCancelInvoice(result.reason);
      }
    });
  }

  private performCancelInvoice(reason: string): void {
    if (!this.data.invoice) return;

    this.loading = true;
    
    this.invoicesService.cancelInvoice(this.data.invoice.id, { cancelReason: reason }).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastr.success('Factura anulada exitosamente', 'Éxito');
          this.dialogRef.close('cancelled'); // Cerrar con flag de cancelación
        } else {
          this.toastr.error(response.reason || 'Error al anular la factura', 'Error');
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error canceling invoice:', error);
        this.toastr.error('Error al anular la factura', 'Error');
        this.loading = false;
      }
    });
  }

  private performReverseInvoice(): void {
    if (!this.data.invoice) return;

    const planWarning = this.viewInvoiceFiscal?.reversesPlanCoverage
      ? `<strong>Advertencia:</strong> Esta factura otorga cobertura al abono del cliente. Al anularla o revertirla, se pierde la cobertura de ese período si no existen otros pagos que lo cubran, pero el abono no se da de baja.<br><br>`
      : '';

    // Confirmar la reversión
    this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        title: 'Revertir Factura',
        message: `¿Estás seguro que deseas revertir esta factura?<br><br>` +
          `<strong>Comprobante:</strong> ${this.data.invoice.invoiceNumber}<br>` +
          `<strong>Cliente:</strong> ${this.data.invoice.customerName}<br>` +
          `<strong>Monto:</strong> $${this.data.invoice.total.toFixed(2)}<br><br>` +
          (this.isCurrentAccountInvoice(this.data.invoice.invoiceType || this.data.invoice.type)
            ? `Se emitirá una nota de crédito y la deuda saldrá de la cuenta corriente. No se mueve caja.<br><br>`
            : `Se emitirá una nota de crédito y, si hubo cobro de contado, un egreso en caja.<br><br>`) +
          `Si la factura es de venta de pase, también se cancelará el pase vigente asociado.<br><br>` +
          planWarning +
          `Esta acción no se puede deshacer.`,
        confirmText: 'Revertir Factura',
        cancelText: 'Cancelar',
        confirmColor: 'warn'
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.loading = true;

        // Llamar al endpoint de crear nota de crédito
        this.invoicesService.createCreditNoteForCashInvoice(this.data.invoice!.id, {
          reasonCode: 'REFUND',
          notes: 'Reversión de factura de contado'
        }).subscribe({
          next: (response) => {
            this.loading = false;
            if (response && response.requestResult) {
              const result = response.requestResult;
              this.toastr.success(
                `Factura revertida exitosamente. Nota de crédito: ${result.creditNoteId.substring(0, 8)}...`,
                'Éxito'
              );
              this.dialogRef.close('reversed'); // Cerrar con flag de reversión
            } else {
              this.toastr.error('Error al revertir la factura', 'Error');
            }
          },
          error: (error) => {
            this.loading = false;
            console.error('Error reversing invoice:', error);
            const errorMsg = error.error?.reason || error.message || 'Error al revertir la factura';
            this.toastr.error(errorMsg, 'Error');
          }
        });
      }
    });
  }

  getStatusLabel(status: string | undefined): string {
    if (!status) return 'Desconocido';
    
    switch (status) {
      case 'Pending':
        return 'Pendiente';
      case 'Paid':
        return 'Pagada';
      case 'Canceled':
      case 'Cancelled':
        return 'ANULADA';
      default:
        return status;
    }
  }

  getStatusColor(status: string | undefined): string {
    if (!status) return 'basic';
    
    switch (status) {
      case 'Pending':
        return 'accent'; // Naranja para pendiente
      case 'Paid':
        return 'primary'; // Azul para pagada
      case 'Canceled':
      case 'Cancelled':
        return 'warn'; // Rojo para cancelada
      default:
        return 'basic'; // Gris por defecto
    }
  }

  mapChipColor(color: string | undefined): string {
    switch (color) {
      case 'primary':
        return 'custom-chip--primary';
      case 'accent':
        return 'custom-chip--accent';
      case 'warn':
        return 'custom-chip--warn';
      default:
        return 'custom-chip--default';
    }
  }

  getInvoiceTypeLabel(invoiceType: string | undefined): string {
    if (!invoiceType) return 'N/A';

    switch (invoiceType) {
      case 'CASH':
      case 'MANUAL':
      case 'Cash':
        return 'Contado';
      case 'CURRENT_ACCOUNT':
      case 'CurrentAccount':
        return 'Cuenta Corriente';
      default:
        return invoiceType || 'N/A';
    }
  }

  getInvoiceTypeColor(invoiceType: string | undefined): string {
    if (!invoiceType) return 'basic';

    switch (invoiceType) {
      case 'CASH':
      case 'MANUAL':
      case 'Cash':
        return 'primary'; // Azul para contado
      case 'CURRENT_ACCOUNT':
      case 'CurrentAccount':
        return 'accent'; // Naranja para cuenta corriente
      default:
        return 'basic'; // Gris por defecto
    }
  }

  isCurrentAccountInvoice(invoiceType?: string): boolean {
    const type = (
      invoiceType ??
      this.invoiceForm.get('invoiceType')?.value ??
      this.data.invoice?.invoiceType ??
      this.data.invoice?.type ??
      ''
    ).toString();

    return type === 'CURRENT_ACCOUNT' || type === 'CurrentAccount';
  }

  shouldShowDueDates(): boolean {
    return this.isViewMode && this.isCurrentAccountInvoice();
  }

  getAuthorizationStatusLabel(status?: number): string {
    switch (status) {
      case 1:
        return 'Autorizada';
      case 0:
        return 'Borrador';
      case 2:
        return 'Rechazada';
      case 3:
        return 'Anulada';
      default:
        return '—';
    }
  }

  async renderAuthorizationQr(invoice: InvoiceDetails): Promise<void> {
    this.authorizationQrDataUrl = null;
    if (!invoice.authorizationQrAvailable || !invoice.authorizationQrUrl) {
      return;
    }
    try {
      this.authorizationQrDataUrl = await QRCode.toDataURL(invoice.authorizationQrUrl, {
        width: 160,
        margin: 1
      });
    } catch {
      this.authorizationQrDataUrl = null;
    }
  }

  printDocument(): void {
    window.print();
  }

  /**
   * Formatea la fecha para enviar al API sin problemas de zona horaria
   * Convierte la fecha local a ISO string pero manteniendo la fecha local
   */
  formatDateForAPI(date: Date): string {
    const d = date instanceof Date && !isNaN(date.getTime()) ? date : new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private needsConsumerIdentification(total: number): boolean {
    return this.consumerIdentificationThreshold > 0 && total >= this.consumerIdentificationThreshold;
  }

  private isCfCustomerWithoutDoc(customer: any): boolean {
    if (!customer) return false;
    const code = String(customer.taxCategoryCode ?? '').trim().toUpperCase();
    const isCf = code === 'CF' || customer.isGenericConsumer === true;
    const hasDoc = !!(String(customer.docNumber ?? '').trim() || String(customer.cuit ?? '').trim());
    return isCf && !hasDoc;
  }
}
